import { useIntl } from 'react-intl';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import AcceptDecision from './AcceptDecision';
import EarlyEndClause from './EarlyEndClause';
import { Field, TextBlock } from './OrderFields';
import { OrderDetails, OrderSummary } from './types';
import {
  canConsultantAccept,
  canConsultantReject,
  getDaysUntil,
} from './utils';
import * as Styles from './Orders.css';

// Details of a waiting order, fetched and cached by the list so a panel that
// remounts or reopens does not load them again.
export type WaitingOrderDetails =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; details: OrderDetails };

interface WaitingOrderPanelProps {
  order: OrderSummary;
  // Undefined until the list starts loading the details.
  entry?: WaitingOrderDetails;
  onRetry: () => void;
  // Active projects of the order's client the consultant is assigned to;
  // null when they could not be loaded, so the field is left out.
  projects: string[] | null;
  busy: boolean;
  onAccept: () => unknown;
  onReject: (reason: string) => unknown;
}

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

// A waiting order opened in the consultant's list (change request of 06.10):
// everything needed for the decision, then the confirmation checkbox with
// Accept and Reject.
const WaitingOrderPanel = ({
  order,
  entry,
  onRetry,
  projects,
  busy,
  onAccept,
  onReject,
}: WaitingOrderPanelProps) => {
  const intl = useIntl();
  const state = entry?.status || 'loading';
  const details = entry?.status === 'ready' ? entry.details : null;

  const daysLeft = getDaysUntil(order.decisionDeadline);
  const subject = (details?.subject ?? order.subject)?.trim();

  return (
    <Box py={2} px={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gridGap={8}
      >
        <Typography variant="subtitle1" component="h2">
          {intl.formatMessage(
            { id: 'MY_ORDERS.DECISION_HEADER' },
            {
              number: order.orderNumber,
              date: formatDate(order.decisionDeadline),
            },
          )}
        </Typography>
        {daysLeft >= 0 && (
          <Typography variant="body2">
            {intl.formatMessage(
              { id: 'ORDERS.NOTE.DAYS_LEFT' },
              { count: daysLeft },
            )}
          </Typography>
        )}
      </Box>
      {state === 'loading' && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress
            size={24}
            aria-label={intl.formatMessage({ id: 'ORDERS.LOADING' })}
          />
        </Box>
      )}
      {state === 'error' && (
        <Box mt={1}>
          <Alert
            severity="error"
            action={
              <Button onClick={onRetry}>
                {intl.formatMessage({ id: 'ORDERS.RETRY' })}
              </Button>
            }
          >
            {intl.formatMessage({ id: 'ORDERS.DETAILS_ERROR' })}
          </Alert>
        </Box>
      )}
      {state === 'ready' && details && (
        <>
          <Styles.DetailGrid>
            <Field
              label={intl.formatMessage({ id: 'ORDERS.CLIENT' })}
              value={order.clientName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.PERIOD' })}
              value={`${formatDate(order.startDate)} – ${formatDate(
                order.endDate,
              )}`}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.OWNER' })}
              value={details.order.ownerName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.CREATED_BY' })}
              value={details.order.createdByName}
            />
            <Field
              label={intl.formatMessage({ id: 'ORDERS.ISSUED_AT' })}
              value={moment(details.order.creationDateTime).format(
                'DD/MM/YYYY HH:mm',
              )}
            />
            {projects && projects.length > 0 && (
              <Field
                label={intl.formatMessage({ id: 'MY_ORDERS.PROJECTS' })}
                value={projects.join(', ')}
              />
            )}
          </Styles.DetailGrid>
          {subject && (
            <TextBlock
              label={intl.formatMessage({ id: 'ORDERS.SUBJECT' })}
              value={subject}
            />
          )}
          <TextBlock
            label={intl.formatMessage({ id: 'ORDERS.CONDITIONS' })}
            value={details.conditions?.trim() || '—'}
          />
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(
                { id: 'MY_ORDERS.DECISION_INFO' },
                { date: formatDate(order.decisionDeadline) },
              )}
            </Typography>
            <EarlyEndClause />
          </Box>
          <AcceptDecision
            orderNumber={order.orderNumber}
            canAccept={canConsultantAccept(details.order)}
            canReject={canConsultantReject(details.order)}
            busy={busy}
            onAccept={onAccept}
            onReject={onReject}
          />
        </>
      )}
    </Box>
  );
};

export default WaitingOrderPanel;
